import { UserWithOrganizationRole } from '@robocorp/robocloud-internal-cds-sdk';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { organizationService } from '../../services/organizationAPI';
import { formatErrorPayload, validateApiResponse } from '../utils';
import { getOrganizationsByShortNameQueryKey, getOrganizationsQueryKey } from '.';

export enum OrganizationMembersErrors {
  GET_ORGANIZATION_MEMBERS_ERROR = 'GET_ORGANIZATION_MEMBERS_ERROR',
  REMOVE_ORGANIZATION_MEMBER_ERROR = 'REMOVE_ORGANIZATION_MEMBER_ERROR',
}

export const getOrganizationMembersQueryKey = (organizationId: string) => [
  'organizationMembers',
  organizationId,
];

export function useOrganizationMembersQuery(
  organizationId: string,
  hasPermission: boolean | undefined = true,
) {
  const dispatch = useDispatch();
  return useQuery(
    getOrganizationMembersQueryKey(organizationId),
    async () => {
      const response = validateApiResponse(
        await organizationService.getOrganizationMembers(organizationId),
      );
      return response.data;
    },
    {
      enabled: hasPermission,
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationMembersErrors.GET_ORGANIZATION_MEMBERS_ERROR,
            organizationId,
            showSnackbar: false,
          }),
        ),
    },
  );
}

export function useOrganizationRemoveMemberMutation(organizationId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ userId }: { userId: string }) => {
      const response = validateApiResponse(
        await organizationService.removeOrganizationMember(organizationId, userId),
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(getOrganizationsQueryKey());
        queryClient.invalidateQueries(getOrganizationsByShortNameQueryKey());
        queryClient.invalidateQueries(getOrganizationMembersQueryKey(organizationId));
      },
      onError: (error: Error) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationMembersErrors.REMOVE_ORGANIZATION_MEMBER_ERROR,
          }),
        );
      },
    },
  );
}

export function useOrganizationSetRoleMutation(organizationId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation<
    UserWithOrganizationRole | undefined,
    Error,
    { userId: string; roleId: string },
    { previousMembers: UserWithOrganizationRole[] | null }
  >(
    async ({ userId, roleId }: { userId: string; roleId: string }) => {
      const response = validateApiResponse(
        await organizationService.setOrganizationRole(organizationId, userId, roleId),
      );
      return response.data;
    },
    {
      onMutate: async ({ userId, roleId }) => {
        await queryClient.cancelQueries(getOrganizationMembersQueryKey(organizationId));

        const previousMembers =
          queryClient.getQueryData<UserWithOrganizationRole[]>(
            getOrganizationMembersQueryKey(organizationId),
          ) ?? [];

        const idx = previousMembers.findIndex(({ id }) => id === userId);
        if (idx === -1) return { previousMembers: null };

        const next = [...previousMembers];
        const updated = {
          ...next[idx],
          organizationRole: {
            ...next[idx].organizationRole,
            id: roleId,
            name: roleId,
          },
        };
        next.splice(idx, 1, updated);

        queryClient.setQueryData(getOrganizationMembersQueryKey(organizationId), next);

        return { previousMembers };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(getOrganizationsByShortNameQueryKey());
        queryClient.invalidateQueries(getOrganizationMembersQueryKey(organizationId));
      },
      onError: (error: Error, variables, context) => {
        dispatch(
          formatErrorPayload(error, {
            type: OrganizationMembersErrors.REMOVE_ORGANIZATION_MEMBER_ERROR,
          }),
        );

        queryClient.setQueryData(
          getOrganizationMembersQueryKey(organizationId),
          context?.previousMembers,
        );
      },
    },
  );
}
